import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { CorrelationConfig } from '../../types/pipelineConfig';
import { TimeWindowInput } from './TimeWindowInput';
import { useAuth } from '../../contexts/AuthContext';
import { useCorrelationRulesQuery } from '../../hooks/queries/useCorrelationRulesQuery';
import { StepConfigHeader } from './StepConfigHeader';
import { getDefaultCorrelationConfig } from '../../utils/pipelineDefaults';

interface CorrelationConfigEditorProps {
  config?: CorrelationConfig;
  onChange: (config: CorrelationConfig) => void;
  endpointType: string | number;
  tenantId?: string;
  disabled?: boolean;
  isSystemDefault?: boolean;
  hideToggle?: boolean;
}

function FieldLabel({ label, helper }: { label: string; helper?: string }) {
  return (
    <div className="mb-1.5">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      {helper && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{helper}</p>
      )}
    </div>
  );
}

export function CorrelationConfigEditor({
  config: rawConfig,
  onChange,
  endpointType,
  tenantId,
  disabled = false,
  isSystemDefault = false,
  hideToggle,
}: CorrelationConfigEditorProps) {
  const defaultConfig = useMemo(() => getDefaultCorrelationConfig(endpointType), [endpointType]);
  const config = useMemo<CorrelationConfig>(() => ({
    ...defaultConfig,
    ...(rawConfig ?? {}),
    correlationRuleIds: rawConfig?.correlationRuleIds ?? defaultConfig.correlationRuleIds ?? [],
  }), [defaultConfig, rawConfig]);
  const { user } = useAuth();
  const isStepEnabled = (config as CorrelationConfig & { isEnabled?: boolean }).isEnabled ?? true;
  const resolvedTenantId = tenantId ?? user?.tenantId ?? '';
  const canQueryRuleLibrary = !isSystemDefault && !!resolvedTenantId;

  const enabledRulesQuery = useCorrelationRulesQuery(
    resolvedTenantId,
    { isEnabled: true },
  );

  const enabledRules = useMemo(
    () => enabledRulesQuery.data ?? [],
    [enabledRulesQuery.data],
  );

  const selectedRuleIds = config.correlationRuleIds ?? [];

  const updateConfig = useCallback((patch: Partial<CorrelationConfig>) => {
    onChange({ ...config, ...patch, useTenantRuleLibrary: true });
  }, [config, onChange]);

  const toggleRuleSelection = useCallback((ruleId: string) => {
    const current = new Set(config.correlationRuleIds ?? []);
    if (current.has(ruleId)) {
      current.delete(ruleId);
    } else {
      current.add(ruleId);
    }

    updateConfig({ correlationRuleIds: Array.from(current) });
  }, [config.correlationRuleIds, updateConfig]);

  return (
    <div className="space-y-6">
        <StepConfigHeader
          title="Correlation"
          isEnabled={isStepEnabled}
          onToggleEnabled={(enabled) =>
            onChange({ ...config, isEnabled: enabled } as CorrelationConfig & { isEnabled: boolean })
          }
          isSystemDefault={isSystemDefault}
          disabled={disabled}
          hideToggle={hideToggle}
        />
      <div className={isStepEnabled ? 'space-y-6' : 'space-y-6 opacity-50 pointer-events-none'}>

      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" aria-label="Correlation global settings">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span>⏱️ Correlation Global Settings</span>
          <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{config.defaultTimeWindowMinutes}m</span>
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-5">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Global Settings</h4>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              These values apply across rule evaluation and fallback behavior.
            </p>
          </div>

          <div>
            <FieldLabel
              label="Default Time Window"
              helper="Fallback window (minutes) when a selected correlation rule does not define one."
            />
            <TimeWindowInput
              value={config.defaultTimeWindowMinutes}
              onChange={(defaultTimeWindowMinutes) => updateConfig({ defaultTimeWindowMinutes })}
              disabled={disabled}
            />
          </div>

          <div>
            <FieldLabel
              label="Max Group Size"
              helper="Maximum number of alerts in a single episode."
            />
            <input
              type="number"
              min={1}
              className="input w-32"
              value={config.maxCorrelationGroupSize}
              onChange={(e) => updateConfig({ maxCorrelationGroupSize: Number(e.target.value) || 1 })}
              disabled={disabled}
            />
          </div>

          <label className="inline-flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.enableTopologyCorrelation}
              onChange={(e) => updateConfig({ enableTopologyCorrelation: e.target.checked })}
              disabled={disabled}
              className="mt-0.5 h-4 w-4 rounded"
            />
            <span>
              <span className="font-medium">Enable Topology Correlation</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Allows topology-aware methods when rules are configured to use them.
              </span>
            </span>
          </label>
        </div>
      </details>

      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span>📚 Correlation Rules Library</span>
          <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">
            {selectedRuleIds.length === 0 ? 'All enabled rules' : `${selectedRuleIds.length} selected`}
          </span>
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            This pipeline step reads correlation rules from your tenant rule library. Manage rule logic in{' '}
            {isSystemDefault ? (
              <span className="font-medium">Correlation Rules</span>
            ) : (
              <Link className="text-primary-600 dark:text-primary-400 hover:underline" to="/correlation-rules">
                Correlation Rules
              </Link>
            )}
            .
          </p>

          {!canQueryRuleLibrary ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
              Rule selection is available in tenant pipeline configuration. System defaults apply to all enabled tenant rules.
            </div>
          ) : enabledRulesQuery.isLoading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">Loading enabled correlation rules…</div>
          ) : enabledRules.length === 0 ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
              No enabled rules found. Create and enable rules in the Correlation Rules page.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Select specific rules for this pipeline. Leave empty to evaluate all enabled rules.
                </p>
                <button
                  type="button"
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  onClick={() => updateConfig({ correlationRuleIds: [] })}
                  disabled={disabled}
                >
                  Use all enabled rules
                </button>
              </div>

              <div className="max-h-56 overflow-auto rounded-md border border-gray-200 dark:border-gray-700">
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {enabledRules.map((rule) => (
                    <li key={rule.id} className="px-3 py-2">
                      <label className="flex items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded"
                          checked={selectedRuleIds.includes(rule.id)}
                          onChange={() => toggleRuleSelection(rule.id)}
                          disabled={disabled}
                        />
                        <span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{rule.name}</span>
                          <span className="block text-xs text-gray-500 dark:text-gray-400">
                            {rule.correlationType} · {rule.timeWindowMinutes}m · {rule.correlationKeyFields}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </details>
      </div>
    </div>
  );
}
